/**
 * AvailabilityConflictAlert - Shows conflict information when a booking slot is unavailable
 */
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, Clock, Calendar, RefreshCw } from 'lucide-react';

export default function AvailabilityConflictAlert({ 
  conflict, 
  onSelectSlot, 
  onRefresh,
  className = ''
}) {
  if (!conflict || !conflict.has_conflict) {
    return null;
  }

  const conflictTypeLabels = {
    'full_day': 'Fully Booked',
    'time_overlap': 'Time Conflict',
    'preparation_window': 'Buffer Time Required',
    'blocked_date': 'Date Blocked',
    'max_capacity': 'Maximum Bookings Reached',
    'tour_conflict': 'Tour Scheduled'
  };

  return (
    <Alert variant="destructive" className={`border-orange-200 bg-orange-50 ${className}`}>
      <AlertTriangle className="h-5 w-5 text-orange-600" />
      <AlertTitle className="text-orange-800 flex items-center gap-2">
        {conflictTypeLabels[conflict.conflict_type] || 'Booking Conflict'}
        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
          {conflict.conflict_type?.replace('_', ' ')}
        </Badge>
      </AlertTitle>
      <AlertDescription className="text-orange-700 mt-2">
        <p>{conflict.message}</p>
        
        {/* Show conflicting booking details if available */}
        {conflict.conflicting_booking && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-orange-200">
            <p className="text-sm font-medium text-gray-700">Existing Booking:</p>
            <div className="flex items-center gap-2 mt-1 text-sm">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>
                {conflict.conflicting_booking.start_time} - {conflict.conflicting_booking.end_time}
              </span>
              {conflict.conflicting_booking.event_type && (
                <Badge variant="secondary" className="text-xs">
                  {conflict.conflicting_booking.event_type}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Show available alternative slots */}
        {conflict.available_slots && conflict.available_slots.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Available time slots on this date:
            </p>
            <div className="flex flex-wrap gap-2">
              {conflict.available_slots.map((slot, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="bg-white hover:bg-green-50 hover:border-green-300"
                  onClick={() => onSelectSlot?.(slot)}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {slot.start} - {slot.end}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* No available slots */}
        {(!conflict.available_slots || conflict.available_slots.length === 0) && (
          <p className="mt-3 text-sm">
            Please select a different date or contact the provider directly.
          </p>
        )}

        {/* Refresh button */}
        {onRefresh && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-3 text-orange-700 hover:text-orange-800 hover:bg-orange-100"
            onClick={onRefresh}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Check Again
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * AvailabilityChecker - Hook and utility for checking availability
 */
export function useAvailabilityCheck(providerId) {
  const [checking, setChecking] = React.useState(false);
  const [conflict, setConflict] = React.useState(null);

  const checkAvailability = async (eventDate, startTime, endTime) => {
    if (!providerId || !eventDate) {
      return { has_conflict: false };
    }

    setChecking(true);
    setConflict(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/availability/events/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: providerId,
          event_date: eventDate,
          start_time: startTime,
          end_time: endTime
        })
      });

      if (response.ok) {
        const result = await response.json();
        setConflict(result.has_conflict ? result : null);
        return result;
      }
    } catch (error) {
      console.error('Availability check failed:', error);
    } finally {
      setChecking(false);
    }

    return { has_conflict: false };
  };

  const clearConflict = () => setConflict(null);

  return {
    checking,
    conflict,
    checkAvailability,
    clearConflict
  };
}
